/**
 * Guide Me — TourLogPanel
 *
 * A right-side slide-in panel that accumulates every narration event while a
 * tour is running. Older entries fade and stay visible so the user can see the
 * full history. Once the tour ends (finished / handoff / aborted) a completion
 * banner is shown with a "Back to walkthroughs" button that restores the card
 * menu.
 */

import { useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { applyLocale, pickTourLocaleKey } from '@empowernow/etl-tour';
import type { Tour, TourLogEntry } from './types';

/** Maps step types to colour-coded pill backgrounds. */
const TYPE_COLOUR: Record<string, string> = {
  narrate:  'rgba(99,102,241,0.22)',
  navigate: 'rgba(34,197,94,0.22)',
  scrollTo: 'rgba(168,85,247,0.20)',
  move:     'rgba(14,165,233,0.22)',
  hover:    'rgba(14,165,233,0.18)',
  click:    'rgba(245,158,11,0.22)',
  type:     'rgba(16,185,129,0.22)',
  press:    'rgba(16,185,129,0.18)',
  select:   'rgba(236,72,153,0.22)',
  waitFor:  'rgba(148,163,184,0.18)',
  wait:     'rgba(148,163,184,0.15)',
  handoff:  'rgba(245,158,11,0.28)',
};

const STEP_META: Record<string, { label: string; icon: string }> = {
  narrate:  { label: 'Note',       icon: '💬' },
  navigate: { label: 'Navigate',   icon: '📍' },
  scrollTo: { label: 'Scroll',     icon: '📜' },
  move:     { label: 'Pointer',    icon: '🎯' },
  hover:    { label: 'Hover',      icon: '🖱️' },
  click:    { label: 'Click',      icon: '👆' },
  type:     { label: 'Type',       icon: '⌨️' },
  press:    { label: 'Key',        icon: '⌨️' },
  select:   { label: 'Select',     icon: '🔽' },
  waitFor:  { label: 'Wait',       icon: '⏳' },
  wait:     { label: 'Pause',      icon: '⏳' },
  handoff:  { label: 'Your turn',  icon: '🤝' },
};

interface TourLogPanelProps {
  tour: Tour;
  entries: TourLogEntry[];
  /** Called when the user clicks "Back to walkthroughs". */
  onBack: () => void;
  /** Called when the user clicks the × close button. */
  onClose: () => void;
  /** Experience shell `resolvedLocale` — matched to `tour.locales` for headings. */
  appLocale: string;
}

export function TourLogPanel({ tour, entries, onBack, onClose, appLocale }: TourLogPanelProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  /* Auto-scroll to the latest entry whenever a new one arrives. */
  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }
  }, [entries.length]);

  /* Determine terminal state from the last entry (if any). */
  const lastEntry = entries.length > 0 ? entries[entries.length - 1] : null;
  const terminalStatus = lastEntry?.terminalStatus ?? null;
  const isTerminal = terminalStatus !== null;

  if (typeof document === 'undefined') return null;

  const localeKey = useMemo(() => pickTourLocaleKey(appLocale, tour.locales), [appLocale, tour.locales]);
  /** Title resolved for the shell locale when an overlay exists. */
  const displayTitle = useMemo(() => applyLocale(tour, localeKey).title, [tour, localeKey]);

  const panel = (
    <div
      data-testid="guide-me-log-panel"
      className="guide-me-log-panel gm-surface"
      style={{
        position: 'fixed',
        background: 'var(--gm-log-panel)',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
        /* Must be above TourPlayer's bubble (1 000 000) and cursor (999 999). */
        zIndex: 1000002,
      }}
    >
      {/* ---------- Header ---------- */}
      <div style={{
        padding: '14px 16px 12px',
        borderBottom: '1px solid var(--gm-border-soft)',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        flexShrink: 0,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'var(--gm-text-accent)',
            marginBottom: 3,
          }}>
            Guide Me · Tour Log
          </div>
          <div style={{
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--gm-text)',
            lineHeight: 1.3,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {displayTitle}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close log panel"
          style={{
            background: 'var(--gm-surface-2)',
            border: '1px solid var(--gm-border-soft)',
            borderRadius: 8,
            color: 'var(--gm-text-dim)',
            cursor: 'pointer',
            fontSize: 14,
            lineHeight: 1,
            padding: '5px 8px',
            flexShrink: 0,
          }}
        >
          ✕
        </button>
      </div>

      {/* ---------- Log entries ---------- */}
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '12px 14px',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        {entries.length === 0 && (
          <div style={{ color: 'var(--gm-text-faint)', fontSize: 13, textAlign: 'center', marginTop: 32 }}>
            Tour steps will appear here as they run…
          </div>
        )}

        {entries.map((entry, idx) => {
          const isLast = idx === entries.length - 1;
          const meta = STEP_META[entry.stepType] ?? STEP_META.narrate;
          const bgColor = TYPE_COLOUR[entry.stepType] ?? 'rgba(99,102,241,0.15)';
          const isOld = !isLast && !entry.terminalStatus;
          return (
            <div
              key={entry.id}
              style={{
                background: isLast ? bgColor : 'var(--gm-surface-2)',
                border: isLast
                  ? `1px solid ${bgColor.replace('0.', '0.5').replace(',0.', ',0.4,')}`
                  : '1px solid var(--gm-border-soft)',
                borderRadius: 10,
                padding: '9px 12px',
                opacity: isOld ? 0.58 : 1,
                transition: 'opacity 300ms',
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                marginBottom: 4,
              }}>
                <span style={{ fontSize: 15, lineHeight: 1 }}>{meta.icon}</span>
                <span style={{
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: isLast ? 'var(--gm-text-accent)' : 'var(--gm-text-faint)',
                }}>
                  {meta.label}
                </span>
                <span style={{ flex: 1 }} />
                <span style={{
                  fontSize: 10,
                  color: 'var(--gm-text-faint)',
                }}>
                  step {entry.stepIndex + 1}
                </span>
              </div>
              <div style={{
                fontSize: 13,
                lineHeight: 1.5,
                color: isLast ? 'var(--gm-text)' : 'var(--gm-text-dim)',
                wordBreak: 'break-word',
              }}>
                {entry.message}
              </div>
            </div>
          );
        })}
      </div>

      {/* ---------- Footer ---------- */}
      <div style={{
        borderTop: '1px solid var(--gm-border-soft)',
        padding: '12px 14px 16px',
        flexShrink: 0,
      }}>
        {isTerminal ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{
              fontSize: 13,
              color: terminalStatus === 'finished'
                ? 'var(--gm-ok)'
                : terminalStatus === 'handoff'
                ? 'var(--gm-warn)'
                : 'var(--gm-danger)',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontWeight: 600,
            }}>
              {/* `color: inherit` keeps the label on the parent's status color
                  (green/yellow/red); an inline style beats the shell's
                  non-!important `[data-theme] span` rule that would otherwise
                  darken it on this dark panel. */}
              {terminalStatus === 'finished' && <><span>✅</span><span style={{ color: 'inherit' }}>Tour complete!</span></>}
              {terminalStatus === 'handoff' && <><span>🤝</span><span style={{ color: 'inherit' }}>Your turn — the guide stopped here.</span></>}
              {terminalStatus === 'aborted' && <><span>⚠️</span><span style={{ color: 'inherit' }}>Tour stopped — element not found.</span></>}
            </div>
            <button
              type="button"
              onClick={onBack}
              style={{
                background: 'linear-gradient(135deg, #4f46e5, #6366f1)',
                color: 'white',
                border: 'none',
                borderRadius: 10,
                padding: '10px 16px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
              }}
            >
              ← Back to walkthroughs
            </button>
          </div>
        ) : (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            color: 'var(--gm-text-dim)',
            fontSize: 12,
          }}>
            <span style={{ fontSize: 14 }}>▶</span>
            {/* `color: inherit` inline beats the shell's non-!important
                `[data-theme] span` rule, keeping the light text on this dark panel. */}
            <span style={{ color: 'inherit' }}>Tour is running…</span>
            <span style={{
              marginLeft: 'auto',
              fontSize: 11,
              color: 'var(--gm-text-accent)',
            }}>
              {entries.filter(e => !e.terminalStatus).length} steps logged
            </span>
          </div>
        )}
      </div>

      {/* Slide-in keyframe injected once. */}
      <style>{`
        @keyframes guidemeLogSlideIn {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0);   opacity: 1; }
        }
      `}</style>
    </div>
  );

  return createPortal(panel, document.body);
}
